import { z } from "zod";

/**
 * A plant of `GET /public_relations/calculator/plants` (`ExplorerPlant`), whose
 * royalty is for sale. Only the fields the explorer consumes are typed (zod drops
 * the rest). Rates are fractions (`royalty` 0.3 = 30%); `initial_investment` is the
 * buyer's upfront for 100% of the plant; `route_to_market` is sorted by share of
 * production, largest first; `location` is region + country code only;
 * `peak_power` (kWp) and `yearly_production` (kWh) cover 100% of the plant.
 */
export const PlantSchema = z.object({
  hash: z.string(),
  image: z.string(),
  currency: z.string(),
  location: z.string(),
  obfuscated_latitude: z.number(),
  obfuscated_longitude: z.number(),
  peak_power: z.number(),
  yearly_production: z.number(),
  specific_production: z.number(),
  producer_type: z.string(),
  route_to_market: z.array(z.string()),
  rental_period: z.number(),
  initial_investment: z.number(),
  royalty: z.number(),
  /** Value proposition this plant illustrates on the solution pages. */
  value_proposition: z.string().default(""),
  /** Stable slug of `value_proposition`, matched against the site's VP slugs. */
  value_proposition_slug: z.string().default(""),
});
export type Plant = z.infer<typeof PlantSchema>;

const PlantPinSchema = z
  .object({
    hash: z.string().optional(),
    obfuscated_latitude: z.number(),
    obfuscated_longitude: z.number(),
    image: z.string().optional(),
    power_rented: z.number(),
    panels_rented: z.number(),
  })
  .catchall(z.unknown());
export type PlantPin = z.infer<typeof PlantPinSchema>;

/**
 * Config only. The card's editorial copy (name, kind, fit) lives in i18n, keyed by
 * plant `id` (`companies-product` → `plants.{id}`), not on this object.
 */
const ExplorerPlantSchema = z.object({
  id: z.string(),
  kwp: z.number(),
  image: z.string(),
  flag: z.string(),
  annualGWh: z.number(),
  revenue: z.enum(["Fisso", "Variabile"]),
});
export type ExplorerPlant = z.infer<typeof ExplorerPlantSchema>;
